import { useQuery } from '@apollo/client';
import { gql, QueryHookOptions } from '@provision/graphql-client';
import { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { META_DATA_QUERY_FIELDS } from 'src/graphql/entities/metadata.fragments';
import { ConditionInput, QueryVariables, Records, SortingInput } from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';
import { BLOCKED_EMAIL_FRAGMENT, BLOCKED_EMAIL_RELATIONS } from './blocked-email.fragments';
import { BlockedEmailEntity } from './blocked-email.types';

const GET_BLOCKED_EMAIL_QUERY = gql`
  query GetBlockedEmail($params: QueryParams, $skipTotalRecords: Boolean = false)
  @entity(name: "BlockedEmail") {
    getBlockedEmail(queryParams: $params) {
      records {
        ...BlockedEmailFields
        ...BlockedEmailRelations
      }
      metadata {
        ...MetaDataQueryFields
      }
    }
  }
  ${BLOCKED_EMAIL_FRAGMENT}
  ${BLOCKED_EMAIL_RELATIONS}
  ${META_DATA_QUERY_FIELDS}
`;

export type GetBlockedEmailQuery = {
  getBlockedEmail: Records<BlockedEmailEntity>;
};

export type GetBlockedEmailQueryParams = {
  filters?: ConditionInput[];
  sorting?: SortingInput[];
  limit?: number;
  offset?: number;
};

export const useBlockedEmail = (
  params: GetBlockedEmailQueryParams = {},
  options: QueryHookOptions<GetBlockedEmailQuery> = {},
) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert } = useNotifications();

  const filters = useMemo(() => {
    // Query blocked emails from platform partner as they are globally applied to all partners.
    const partnerIds = [PLATFORM_PARTNER_ID];
    if (selectedPartnerId) {
      partnerIds.push(selectedPartnerId);
    }
    // enforce filtering by Partner Id as it is mandatory.
    const partnerFilter: ConditionInput = {
      attribute: 'partnerId',
      operator: 'in',
      value: partnerIds.join(','),
    };
    return [
      partnerFilter,
      // apply all other filters provided by caller (except filtering by partnerId).
      ...(params.filters || []).filter((f) => f.attribute !== 'partnerId'),
    ];
  }, [selectedPartnerId, params.filters]);

  const {
    data,
    loading: isLoading,
    fetchMore,
    variables,
  } = useQuery<GetBlockedEmailQuery, QueryVariables>(GET_BLOCKED_EMAIL_QUERY, {
    ...options,
    variables: {
      params: { ...params, filters },
    },
    context: {
      includePlatformLevelData: true,
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to query blocked emails, please try again');
    },
  });

  const records = useMemo(
    () => data?.getBlockedEmail.records || [],
    [data?.getBlockedEmail.records],
  );

  const totalRecords = data?.getBlockedEmail?.metadata?.totalRecords ?? 0;

  const { loadMore, hasReachedEnd } = useLoadMore<GetBlockedEmailQuery>({
    offset: records?.length ?? 0,
    fetchMore,
    params: variables?.params!,
  });

  return {
    blockedEmails: records,
    isLoading,
    loadMore,
    totalRecords,
    hasMore: totalRecords ? records?.length < totalRecords : !hasReachedEnd,
  };
};
